import { LEFT_MENU_COMPANY, LEFT_MENU_SEEKER } from 'constants/seekerConfig';
import React, { useState } from 'react';
import { Accordion, Form, Nav } from 'react-bootstrap';
import useAuthStore, { resetState, setUserInfo } from 'store/auth';
import { USER_TYPES } from 'constants/index';
import { Link, useLocation, useParams } from 'react-router-dom';
import { logoutZIM, useChange2FAStatusMutation, useLogoutMutation } from 'services';
import { setModalConfig } from 'store/common';
import toast from 'react-hot-toast';
import { disconnect } from 'store/socket';
import useLocalizationStore from 'store/localization';
import { resetFeeds } from 'store/feeds';
import useTranslate from 'hooks/useTranslate';
import './styles.scss';
import { resetAllStores } from 'store/resetStore';
import { clearStorageExceptPrefix } from 'utils';
import { secureStorage } from 'utils/secureStorage';

const LeftMenus: React.FC<any> = (props: any) => {
	const { activeLanguage }: any = useLocalizationStore((state) => state);
	const { user, token }: Record<string, any> = useAuthStore((state) => state.userInfo ?? {});
	const { id } = useParams<any>();
	const [activeKey, setActiveKey] = useState(null);
	const { mutateAsync: change2FAStatus } = useChange2FAStatusMutation();
	const { mutateAsync: logoutMutation } = useLogoutMutation();
	const location = useLocation();
	const { translate } = useTranslate();

	const handleModal = (tabKey: string, modal: string = '') => {
		if (tabKey === 'auth2Factor') {
			const updateStatus = !user?.auth2Factor;
			change2FAStatus({ set2fa: updateStatus })
				.then((res: any) => {
					if (res?.success) {
						setModalConfig({
							visible: true,
							id: id,
							type: modal,
							tabKey: tabKey,
						});
					}
				})
				.catch((error) => {
					console.error('Failed to change 2FA status:', error);
					toast.error(translate('ToastMessage.failedToUpdate2FaStatus')); // Notify the user of the failure
				});
		} else {
			setModalConfig({
				visible: true,
				id: id,
				type: modal,
				tabKey: tabKey,
			});
		}
	};

	const handleLogout = () => {
		logoutMutation()
			.then((res: any) => {
				resetFeeds();
				clearStorageExceptPrefix(sessionStorage, ['uc_']);
				clearStorageExceptPrefix(localStorage, ['uc_', 'i18nextLng']);
				resetAllStores();
				if (res?.statusCode === 401) {
					secureStorage.removeItem('fcmToken');
					logoutZIM();
					disconnect();
					resetState();
				}

				if (res?.success) {
					secureStorage.removeItem('fcmToken');
					logoutZIM();
					disconnect();
					setUserInfo({});
					toast.success(res?.message);
				}
			})
			.catch((error) => {
				console.error('Logout failed:', error);
				toast.error(translate('ToastMessage.anUnexpectedErrorOccurred')); // Notify the user of the failure
			});
	};

	const onAccordionClick = (selectedKey: any) => {
		setActiveKey(selectedKey === activeKey ? null : selectedKey);
	};

	const TAB_CONTENT = user?.userType === USER_TYPES.COMPANY ? LEFT_MENU_COMPANY : LEFT_MENU_SEEKER;

	return (
		<>
			{TAB_CONTENT?.map((menuItem: any, index: number) =>
				menuItem.children ? (
					<Accordion
						key={`accordion-${index}-${menuItem?.tabKey || menuItem?.title[activeLanguage]}`}
						activeKey={activeKey}
						onSelect={onAccordionClick}
						className="user-actions-accordion"
						defaultActiveKey={activeKey}
					>
						<Accordion.Item eventKey={menuItem.tabKey} className="border-0">
							<Accordion.Header>
								{menuItem?.icon && <i className={menuItem.icon}></i>}
								{menuItem.title[activeLanguage]}
							</Accordion.Header>
							<Accordion.Body className="p-0">
								<Nav.Item>
									{menuItem.children.map((child: any, childIndex: number) => {
										return child?.type !== 'logout' ? (
											child?.tabKey === 'change-password' && user?.signUpType !== 'normal' ? (
												<React.Fragment key={`empty-${childIndex}`}></React.Fragment>
											) : (
												<Link
													key={`child-${childIndex}-${child?.tabKey || child?.title[activeLanguage]}`}
													to={child?.redirectTo}
													onClick={(e) => {
														if (!token) {
															e.preventDefault();
															setModalConfig({
																visible: true,
																type: 'loginModal',
															});
														} else {
															if (props?.onClose) {
																props?.onClose();
															}
															child?.type && handleModal(child?.tabKey, child?.modal);
														}
													}}
													className={`user-actions-menu border-0 d-flex align-items-center justify-content-between gap-2 ${location?.pathname === child?.redirectTo ? 'active' : ''}`}
												>
													{child?.icon && <i className={child.icon}></i>}
													{child.title[activeLanguage]}
													{child?.type && (
														<Form>
															<Form.Check
																type="switch"
																id={`custom-switch-${childIndex}`}
																checked={user?.[child?.tabKey] || false}
																onChange={() => {}} // Add empty onChange handler
																className="switch-btn"
															/>
														</Form>
													)}
												</Link>
											)
										) : (
											!props?.isMobile && (
												<div
													key={`logout-${childIndex}-${child?.id || child?.title[activeLanguage]}`}
													className="text-center mt-4"
												>
													<Link
														to={{}}
														onClick={async () => {
															try {
																await handleLogout();
															} catch (error) {
																console.error('Logout failed:', error);
																toast.error(translate('Common.logoutFailed')); // Notify the user of the failure
															}
														}}
														className={`user-signout-menu fw-medium border-0 ${location?.pathname === child?.redirectTo ? translate('Common.active') : ''}`}
													>
														{child?.icon && <i className={child.icon}></i>}
														{child.title[activeLanguage]}
													</Link>
												</div>
											)
										);
									})}
								</Nav.Item>
							</Accordion.Body>
						</Accordion.Item>
					</Accordion>
				) : (
					<Nav.Item key={`menu-${index}-${menuItem?.tabKey || menuItem?.title[activeLanguage]}`}>
						<Link
							to={menuItem?.redirectTo}
							onClick={(e) => {
								if (!token) {
									e.preventDefault();
									setModalConfig({
										visible: true,
										type: 'loginModal',
									});
								} else {
									if (props?.onClose) {
										props?.onClose();
									}
								}
							}}
							className={`user-actions-menu border-0 d-flex align-items-center gap-2 ${location?.pathname === menuItem?.redirectTo ? translate('Common.active') : ''}`}
						>
							<i className={menuItem?.icon}></i>
							{menuItem.title[activeLanguage]}
						</Link>
					</Nav.Item>
				),
			)}
		</>
	);
};

export default LeftMenus;
